import cn from "classnames"
import { lazy, Suspense, useRef, useState } from "react"

import { Collapse, Flex, Skeleton } from "@/atoms"
import { isFirefox } from "@/helpers/utils"
import { useLogger } from "@/hooks/useLogger"
import { useTranslation } from "@/hooks/useTranslation"
import { DownOutlined, UpOutlined } from "@/icons"
import { DataFlow } from "@/organisms/DataFlow"
import { CardIssueDescriptionKai } from "./components/CardIssueDescriptionKai"
import { TitleIssueDescriptionKai } from "./components/TitleIssueDescriptionKai"
import { WorkflowActionIssueDescriptionKai } from "./components/WorkflowActionIssueDescriptionKai"

import styles from "./IssueDescriptionKai.module.scss"

import type { DataFlow as TDataFlow } from "@/domain/data-flow/data-flow.types"
import type { IssueDescriptionKaiProps } from "."

const IconTitleIssue = lazy(() => import("./components/IconTitleIssue"))

export const IssueDescriptionKai = (props: IssueDescriptionKaiProps) => {
  const {
    loading = false,
    irrelevantItems,
    positiveItems,
    undeterminedItems
  } = props

  const size = "small" as const
  const skeletonProps = { size, active: true, className: styles.skeleton }
  const firefox = useRef(isFirefox()).current
  const [flow, setFlow] = useState<TDataFlow | null>(null)
  const { t } = useTranslation("translation", {
    keyPrefix: "triage.drawer.kai.sast"
  })
  const { logger, EVENTS } = useLogger()

  const expandIcon = (props: { children?: string; isActive?: boolean }) => {
    if (loading) return null

    if (props.isActive) return <UpOutlined />

    return <DownOutlined />
  }

  const existFalsePositive = irrelevantItems.length > 0
  const moreThanOneExist =
    [irrelevantItems, positiveItems, undeterminedItems].filter(
      (items) => items.length > 0
    ).length > 1
  const onlyFalsePositive = !moreThanOneExist && existFalsePositive
  const fnProps = { t, setFlow }

  const items = [
    {
      key: "kai-description",
      label: (
        <Flex align="center" justify="space-between">
          <Flex gap={8} align="center">
            <Suspense fallback={<Skeleton.Node {...skeletonProps} />}>
              <IconTitleIssue existFalsePositive={existFalsePositive} />
            </Suspense>
            <Flex vertical gap={8}>
              {loading && (
                <>
                  <Skeleton.Input {...skeletonProps} />
                  <Skeleton.Input {...skeletonProps} />
                </>
              )}
              {!loading && <TitleIssueDescriptionKai {...props} />}
            </Flex>
          </Flex>
          {loading && <Skeleton.Input {...skeletonProps} />}
        </Flex>
      ),
      children: (
        <div className={styles.scrollContainer}>
          <div className={cn(styles.wrapper, firefox && styles.wrapperFirefox)}>
            {!moreThanOneExist && ( // render only one type of items
              <div className={styles.wrapperGrid}>
                {irrelevantItems.map((item, index) => (
                  <CardIssueDescriptionKai key={index} {...fnProps} {...item} />
                ))}
                {undeterminedItems.map((item, index) => (
                  <CardIssueDescriptionKai key={index} {...fnProps} {...item} />
                ))}
                {positiveItems.map((item, index) => (
                  <CardIssueDescriptionKai key={index} {...fnProps} {...item} />
                ))}
              </div>
            )}
            {moreThanOneExist && (
              <Flex vertical gap={24}>
                {irrelevantItems.length > 0 && (
                  <Flex vertical gap={8}>
                    <span>
                      {t("falsePositive", { count: irrelevantItems.length })}
                    </span>
                    <div className={styles.wrapperGrid}>
                      {irrelevantItems.map((item, index) => (
                        <CardIssueDescriptionKai
                          key={index}
                          {...fnProps}
                          {...item}
                        />
                      ))}
                    </div>
                  </Flex>
                )}
                {undeterminedItems.length > 0 && (
                  <Flex vertical gap={8}>
                    <span>
                      {t("undetermined", {
                        count: undeterminedItems.length
                      })}
                    </span>
                    <div className={styles.wrapperGrid}>
                      {undeterminedItems.map((item, index) => (
                        <CardIssueDescriptionKai
                          key={index}
                          {...fnProps}
                          {...item}
                        />
                      ))}
                    </div>
                  </Flex>
                )}
                {positiveItems.length > 0 && (
                  <Flex vertical gap={8}>
                    <span>
                      {t("confirmed", { count: positiveItems.length })}
                    </span>
                    <div className={styles.wrapperGrid}>
                      {positiveItems.map((item, index) => (
                        <CardIssueDescriptionKai
                          key={index}
                          {...fnProps}
                          {...item}
                        />
                      ))}
                    </div>
                  </Flex>
                )}
              </Flex>
            )}
            {onlyFalsePositive && <WorkflowActionIssueDescriptionKai />}
          </div>
        </div>
      )
    }
  ]

  return (
    <>
      <Collapse
        className={styles.container}
        expandIcon={expandIcon}
        expandIconPlacement="end"
        items={items}
        onChange={(key) => {
          if (key?.[0])
            logger.info(
              EVENTS.ANALYTIC_EVENTS.TRIAGE.DRAWER.DROPDOWN_KAI_OCCURRENCES,
              { [key[0]]: "open" }
            )
        }}
      />
      {!!flow && <DataFlow open data={flow} onClose={() => setFlow(null)} />}
    </>
  )
}
